import * as React from 'react';
import Alert from '@mui/material/Alert';
import { useUser } from 'hooks/use-user';
import { logger } from 'lib/default-logger';
import { paths } from 'paths';
import { useNavigate } from 'react-router-dom';

export interface AuthGuardProps {
  children: React.ReactNode;
  roles?: string[];
}

export function AuthGuard({ children, roles }: AuthGuardProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const { user, error, isLoading } = useUser();
  const [isChecking, setIsChecking] = React.useState<boolean>(true);

  const checkPermissions = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    if (error) {
      setIsChecking(false);
      return;
    }

    if (!user) {
      logger.debug('[AuthGuard]: User is not logged in, redirecting to sign in');
      navigate(paths.app.auth.signIn);
      return;
    }

    if (roles && !user.roles?.some((userRole) => roles.some((r) => r === userRole))) {
      logger.debug('[AuthGuard]: User is not authorized to access this page, redirecting to 403 error');
      navigate(paths.errors.notNotAuthorized);
      return;
    }

    setIsChecking(false);
  };

  React.useEffect(() => {
    checkPermissions().catch(() => {
      // noop
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Expected
  }, [user, error, isLoading]);

  if (isChecking) {
    return null;
  }

  if (error) {
    return <Alert color="error">{error}</Alert>;
  }

  return <React.Fragment>{children}</React.Fragment>;
}
